import { validateFieldCaptureTarget } from './field-capture-mode.js';

/**
 * Encodes the exact model identifiers carried by a repeated measurement field.
 * JSON keeps type keys, person slots, and row ids reversible without guessing
 * at separators that those maintained identifiers might legitimately contain.
 */
export function captureEntityId(...parts) {
  return JSON.stringify(parts.map((part) => String(part)));
}

function captureEntityParts(entityId, count) {
  try {
    const parts = JSON.parse(entityId);
    return Array.isArray(parts) && parts.length === count && parts.every((part) => typeof part === 'string')
      ? parts : null;
  } catch {
    return null;
  }
}

function visibleDraftControl(root, target) {
  return [...(root?.querySelectorAll?.('[data-capture-kind]') ?? [])].find((control) => (
    control.dataset.captureKind === target.kind
      && control.dataset.captureEntityId === target.entityId
      && control.dataset.captureProperty === target.property
  )) ?? null;
}

/**
 * Returns the current value shown for a capture destination. This is kept with
 * the writer so the capture dock and Apply action cannot silently disagree
 * about how a repeated target maps back to the canonical sketch model.
 */
export function roomSketchCaptureTargetValue(target, { model, root } = {}) {
  const valid = validateFieldCaptureTarget(target);
  if (valid.kind === 'wall_length') {
    const wall = model.state.walls.find((item) => item.id === valid.entityId);
    return wall ? model.wallLengthUnits(wall) : null;
  }
  if (valid.kind === 'space_field') {
    return valid.property === 'ceilingHeightUnits' ? model.state.ceilingHeightUnits : model.state.doorHeightUnits;
  }
  if (valid.kind === 'wall_field') {
    const wall = model.state.walls.find((item) => item.id === valid.entityId);
    if (!wall) return null;
    if (valid.property === 'wallThicknessUnits') return model.wallThicknessUnits?.(wall) ?? wall.thicknessUnits ?? null;
    if (valid.property === 'partitionDepthUnits') return model.partitionDepthFor(valid.entityId);
    return model.wallLengthUnits(wall);
  }
  if (valid.kind === 'wall_height_field') {
    if (valid.property === 'startHeightUnits') return model.cornerHeightUnits(valid.entityId, 'start');
    if (valid.property === 'endHeightUnits') return model.cornerHeightUnits(valid.entityId, 'end');
    return model.wallStackTargetUnits(valid.entityId);
  }
  if (['wall_height_draft', 'ceiling_opening_draft'].includes(valid.kind)) {
    return visibleDraftControl(root, valid)?.value ?? null;
  }
  if (valid.kind === 'ceiling_opening_field') {
    return model.state.ceilingOpenings.find((item) => item.id === valid.entityId)?.[valid.property] ?? null;
  }
  if (valid.kind === 'design_space_field') return model.state.spaceHeightUnits;
  if (valid.kind === 'section_field') {
    return model.state.sections.find((section) => section.id === valid.entityId)?.pairWidthUnits ?? null;
  }
  if (valid.kind === 'storage_need_field') {
    const parts = captureEntityParts(valid.entityId, 2);
    if (!parts) return null;
    const [typeKey, slot] = parts;
    const field = valid.property === 'existingUnits' ? 'existing' : 'needed';
    return model.storageNeedFor(typeKey)?.[field]?.[slot] ?? null;
  }
  if (valid.kind === 'footwear_field') {
    const parts = captureEntityParts(valid.entityId, 2);
    if (!parts) return null;
    const [typeKey, groupId] = parts;
    return model.storageNeedFor(typeKey)?.shoeGroups?.find((group) => group.id === groupId)?.[valid.property] ?? null;
  }
  const obstruction = model.state.obstructions.find((item) => item.id === valid.entityId);
  if (!obstruction) return null;
  return valid.property === 'referenceOffset' ? obstruction.referenceOffsetUnits : obstruction[valid.property];
}

/**
 * Applies a captured reading through the exact model intent method used by the
 * corresponding manual control. Creation-form targets are the intentional
 * exception: they write the visible draft, whose existing submit handler is
 * the sole route into `addCeilingOpening`.
 */
export function writeRoomSketchCaptureTarget(target, valueUnits, { model, root } = {}) {
  const valid = validateFieldCaptureTarget(target);
  if (valid.kind === 'wall_length') return model.setWallMeasuredLength(valid.entityId, String(valueUnits));
  if (valid.kind === 'space_field') {
    return valid.property === 'ceilingHeightUnits'
      ? model.setCeilingHeight(String(valueUnits))
      : model.setDoorHeight(String(valueUnits));
  }
  if (valid.kind === 'wall_field') {
    if (valid.property === 'wallThicknessUnits') return model.setWallThickness(valid.entityId, String(valueUnits));
    if (valid.property === 'partitionDepthUnits') return model.setPartitionDepth(valid.entityId, String(valueUnits));
    return model.setWallLength(valid.entityId, String(valueUnits));
  }
  if (valid.kind === 'wall_height_field') {
    if (valid.property === 'startHeightUnits') return model.setCornerHeight(valid.entityId, 'start', String(valueUnits));
    if (valid.property === 'endHeightUnits') return model.setCornerHeight(valid.entityId, 'end', String(valueUnits));
    return model.setWallStackTarget(valid.entityId, String(valueUnits));
  }
  if (['wall_height_draft', 'ceiling_opening_draft'].includes(valid.kind)) {
    const control = visibleDraftControl(root, valid);
    if (!control) return false;
    control.value = String(valueUnits);
    return true;
  }
  if (valid.kind === 'ceiling_opening_field') {
    return model.updateCeilingOpening(valid.entityId, { [valid.property]: valueUnits });
  }
  if (valid.kind === 'design_space_field') return model.setSpaceHeight(String(valueUnits));
  if (valid.kind === 'section_field') return model.setSectionDim(valid.entityId, 'pairWidthUnits', String(valueUnits));
  if (valid.kind === 'storage_need_field') {
    const parts = captureEntityParts(valid.entityId, 2);
    if (!parts) return false;
    const [typeKey, slot] = parts;
    const field = valid.property === 'existingUnits' ? 'existing' : 'needed';
    return model.setStorageNeed(typeKey, field, slot, String(valueUnits));
  }
  if (valid.kind === 'footwear_field') {
    const parts = captureEntityParts(valid.entityId, 2);
    if (!parts) return false;
    const [typeKey, groupId] = parts;
    return model.updateShoeInventoryGroup(typeKey, groupId, valid.property, String(valueUnits));
  }
  if (valid.kind === 'opening_field'
    && ['clearOpeningWidthUnits', 'clearOpeningHeightUnits', 'swingProjectionUnits', 'bifoldStackProjectionUnits', 'bypassClearLeftUnits', 'bypassClearRightUnits'].includes(valid.property)) {
    return model.setWallOpening(valid.entityId, { [valid.property]: valueUnits });
  }
  if (valid.property === 'referenceOffset') return model.updateObstructionReferenceOffset(valid.entityId, String(valueUnits));
  return model.updateObstruction(valid.entityId, valid.property, String(valueUnits));
}
